import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../utils/api';

const DEFAULT_CUSTOM_RANGE = { start: '', end: '' };

export function useRestartData(selectedDevice, selectedInterval, customRange = DEFAULT_CUSTOM_RANGE) {
    const [events, setEvents] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchRestarts = useCallback(async () => {
        if (!selectedDevice || !selectedInterval) {
            setEvents([]);
            setMeta(null);
            setLastUpdated(null);
            return;
        }

        if (selectedInterval === 'custom' && (!customRange?.start || !customRange?.end)) {
            setEvents([]);
            setMeta(null);
            setLastUpdated(null);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const extraParams = {
                ...(selectedInterval === 'custom' && customRange?.start && customRange?.end
                    ? { dt_start: customRange.start, dt_end: customRange.end }
                    : {}),
                limit: 500,
            };

            const data = await apiClient.getRestarts(selectedDevice, selectedInterval, extraParams);
            const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];

            setEvents(items);
            setMeta(data?.meta ?? null);
            setLastUpdated(new Date());
        } catch (err) {
            console.error('Error fetching restart events:', err);
            if (err?.status === 403) {
                setError('You do not have permission to view restarts.');
            } else if (err?.status === 401) {
                setError('Session expired. Please sign in again.');
            } else {
                setError(err?.message || 'Unknown error fetching restarts.');
            }
            setEvents([]);
            setMeta(null);
            setLastUpdated(null);
        } finally {
            setLoading(false);
        }
    }, [selectedDevice, selectedInterval, customRange?.start, customRange?.end]);

    useEffect(() => {
        fetchRestarts();
    }, [fetchRestarts]);

    return {
        events,
        meta,
        loading,
        error,
        lastUpdated,
        refresh: fetchRestarts,
    };
}
